'use client'

import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { MouseEvent, ReactElement } from 'react'
import {
  AdvertisingImageModal,
  type AdvertisingImageModalProps,
} from './AdvertisingImageModal'
import { getAdvertisingModalOpenState } from './advertisingModalState'
import type { AdvertisingModalTriggerType } from './types'
import { safeLocalStorage } from './utils/errorHandling'
import { useExitIntent } from './useExitIntent'

export interface AdvertisingImageModalTriggerProps extends Omit<
  AdvertisingImageModalProps,
  'isOpen' | 'onClose'
> {
  trigger: AdvertisingModalTriggerType
  triggerDelay?: number
  exitIntentSensitivity?: number
  children?: ReactElement
  onTrigger?: () => void
  showOnce?: boolean
  cookieName?: string
  cookieExpireDays?: number
}

export const AdvertisingImageModalTrigger = ({
  trigger,
  triggerDelay = 5000,
  exitIntentSensitivity = 30,
  children,
  onTrigger,
  showOnce = false,
  cookieName = 'advertisingImageModalShown',
  cookieExpireDays = 7,
  ...modalProps
}: AdvertisingImageModalTriggerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasTriggered, setHasTriggered] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleTrigger = useCallback(() => {
    if (trigger !== 'click' && hasTriggered) return
    if (getAdvertisingModalOpenState()) return

    if (showOnce) {
      const stored = safeLocalStorage.getItem(cookieName)

      if (stored) {
        try {
          const data = JSON.parse(stored) as { expiry?: string }
          const expiryDate = data.expiry ? new Date(data.expiry) : null
          const now = new Date()

          if (expiryDate && now <= expiryDate) return
          safeLocalStorage.removeItem(cookieName)
        } catch {
          safeLocalStorage.removeItem(cookieName)
        }
      }
    }

    setHasTriggered(true)
    setIsOpen(true)

    if (showOnce) {
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + cookieExpireDays)
      safeLocalStorage.setItem(
        cookieName,
        JSON.stringify({ shown: true, expiry: expiryDate.toISOString() })
      )
    }

    onTrigger?.()
  }, [cookieExpireDays, cookieName, hasTriggered, onTrigger, showOnce, trigger])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const originalOnClick =
        children && isValidElement(children)
          ? (children.props as { onClick?: (event: MouseEvent) => void })
              ?.onClick
          : undefined

      originalOnClick?.(event)
      if (event.defaultPrevented) return
      handleTrigger()
    },
    [children, handleTrigger]
  )

  useEffect(() => {
    if (trigger !== 'timer' || hasTriggered) return

    timerRef.current = setTimeout(handleTrigger, triggerDelay)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [handleTrigger, hasTriggered, trigger, triggerDelay])

  useExitIntent({
    onExitIntent: handleTrigger,
    enabled: trigger === 'exit-intent' && !hasTriggered,
    sensitivity: exitIntentSensitivity,
  })

  const shouldRenderClickWrapper =
    trigger === 'click' && children && isValidElement(children)

  if (shouldRenderClickWrapper) {
    return (
      <>
        {cloneElement(
          children as ReactElement<{ onClick?: (event: MouseEvent) => void }>,
          { onClick: handleClick }
        )}
        <AdvertisingImageModal
          {...modalProps}
          isOpen={isOpen}
          onClose={handleClose}
        />
      </>
    )
  }

  return (
    <AdvertisingImageModal
      {...modalProps}
      isOpen={isOpen}
      onClose={handleClose}
    />
  )
}
